import axios from 'axios';
import { redirect } from 'react-router';
import { authNavigationService } from './AuthNavigationService';
import { Logger } from '../utils/Logger';

/**
 * Handle errors in route loaders with proper 401 (Unauthorized) handling.
 * When user logs out in another tab, other tabs will receive 401 errors.
 * This function redirects to login instead of showing ErrorPage.
 *
 * Usage in loaders:
 * ```
 * try {
 *   const data = await apiService.getData(...);
 *   return { data };
 * } catch (error) {
 *   return handleLoaderError(error, request.url);
 * }
 * ```
 *
 * @param error - The error caught in the loader
 * @param requestUrl - The request URL (from request.url)
 * @returns redirect response if 401 was handled, otherwise throws error
 */
export function handleLoaderError(error: unknown, requestUrl: string): ReturnType<typeof redirect> {
  // Handle 401 (user logged out in another tab)
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const pathname = new URL(requestUrl).pathname;
    const search = new URL(requestUrl).search;
    const currentPath = pathname + search;

    const redirectUrl = authNavigationService.getRedirectUrlFor401(currentPath);
    if (redirectUrl) {
      Logger.debug('LOADER', `401 detected, redirecting to: ${redirectUrl}`);
      return redirect(redirectUrl);
    }
  }

  // React Router redirect responses (3xx) — rethrow silently, React Router les intercepte
  if (error instanceof Response && error.status >= 300 && error.status < 400) {
    throw error;
  }

  // Other errors - throw as is
  Logger.error('LOADER', "Loader failed:", error);
  throw error;
}
